/**
 * Softphone - Enhanced WebRTC Softphone Component
 * 
 * Features:
 * - SIP Registration with failover
 * - Inbound/Outbound calls
 * - Mute/Unmute
 * - Hold/Resume
 * - Blind & Attended Transfer
 * - 3-Way Conference
 * - DTMF dialpad
 * - Audio device selection
 * - Call quality metrics
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSIP, ConnectionState, CallState, TransferType } from '../../hooks/useSIP';
import { usePresenceHeartbeat } from '../../hooks/usePresenceHeartbeat';
import CONFIG from '../../config';
import { softphoneApi, permissionsApi } from '../../api/client';
import './Softphone.css';

// Refresh the SIP bundle a bit before expiry so we never go cold.
const BUNDLE_REFRESH_MARGIN_SEC = 60;

// Sub-components
import LoginForm from './LoginForm';
import CallDisplay from './CallDisplay';
import Dialpad from './Dialpad';
import CallControls from './CallControls';
import HeldCallsTray from './HeldCallsTray';
import MultiLineBar from './MultiLineBar';
import ConferenceBar from './ConferenceBar';
import TransferDialog from './TransferDialog';
import SettingsPanel from './SettingsPanel';
import AgentStatusBar from './AgentStatusBar';
import QosPanel from './QosPanel';
import CallHistoryPanel from './CallHistoryPanel';
import ScreenShareGate from './ScreenShareGate';

export function Softphone({ onLogin, onStatusChange }) {
  const sip = useSIP();

  // 2026-10-12: presence heartbeat. Renews the server-side presence lease while
  // the agent is REGISTERED so the backend can detect a real departure within
  // ~1 min (vs the 16h janitor). Phase 1 = shadow (server observes only).
  usePresenceHeartbeat(sip.connectionState === ConnectionState.REGISTERED);

  // Phase 1: SIP credentials are fetched server-side from /api/softphone/credentials.
  // Agents never see or type extension/password. The portal MFA session gates
  // access to the bundle endpoint; bundle TTL is short and is silently
  // refreshed in the background before it expires.
  const [credsState, setCredsState] = useState('loading'); // loading | ready | error
  const [credsError, setCredsError] = useState(null);
  const credsRef      = useRef(null);   // last bundle, used by the silent refresher
  const credsTimerRef = useRef(null);
  // Legacy form state retained only because some sub-components reference it;
  // the typed login form is no longer rendered.
  const [extension, setExtension] = useState('');
  const [password, setPassword] = useState('');
  const [dialNumber, setDialNumber] = useState('');
  const [showDialpad, setShowDialpad] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  // 2026-07-26: "Add Call" mode. When the agent clicks Add Call we DON'T open
  // the Transfer/Conference dialog (confusing). Instead we put the softphone
  // into add-call mode: reveal the normal dialpad, let the agent type the
  // number, and when they press Call we route it through startConference()
  // — the exact same path as Transfer > Conference (which merges reliably).
  const [addCallMode, setAddCallMode] = useState(false);
  // 2026-08-10: Add-Call FROM INSIDE a conference (routes handleDial to the
  // conference/add-party endpoint instead of the 2-line lineAdd).
  const [confAddMode, setConfAddMode] = useState(false);
  // 2026-07-27: SERVER-SIDE multi-line state. The agent keeps ONE browser leg;
  // Asterisk originates + bridges the 3rd party and swaps the agent's bridge.
  // This local state is just the control-surface view of which line is active.
  //   active: 'caller' | 'third'   which party the agent is talking to
  //   hasThird: bool               a 3rd party leg exists
  //   thirdNumber: string          the dialed 3rd-party number (display)
  //   conf: null | {               2026-08-10 N-party conference appearance
  //     room, partyCount, survivorNumber,
  //     consultActive, consultNumber }
  const [mlState, setMlState] = useState({
    active: 'caller', hasThird: false, thirdNumber: '', conf: null,
  });
  const [showSettings, setShowSettings] = useState(false);
  // 2026-07-28: agent proceeded without screen recording via an allowed
  // tenant bypass (Azure connectivity failed). Drives a persistent banner.
  const [screenRecUnrecorded, setScreenRecUnrecorded] = useState(false);
  const [transferType, setTransferType] = useState(null);
  // Persist last dialed number across reloads so agents can redial easily.
  const [lastDialed, setLastDialed] = useState(() => {
    try { return localStorage.getItem('cx-last-dialed') || ''; } catch { return ''; }
  });

  // QoS sidecar panel — toggle from the top-right QoS button. Persist the
  // open/collapsed preference so re-opening the softphone tab feels stable.
  const [showQos, setShowQos] = useState(() => {
    try { return localStorage.getItem('cx-qos-shown') === '1'; } catch { return false; }
  });
  const [showHistory, setShowHistory] = useState(() => {
    try { return localStorage.getItem('cx-history-shown') === '1'; } catch { return false; }
  });
  const [historyCollapsed, setHistoryCollapsed] = useState(() => {
    try { return localStorage.getItem('cx-history-collapsed') === '1'; } catch { return false; }
  });
  // Bumped whenever a call ends, so the history panel auto-refreshes and the
  // just-ended call (with its caller ID) shows up immediately.
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [qosCollapsed, setQosCollapsed] = useState(() => {
    try { return localStorage.getItem('cx-qos-collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cx-qos-shown', showQos ? '1' : '0'); } catch {}
  }, [showQos]);
  useEffect(() => {
    try { localStorage.setItem('cx-qos-collapsed', qosCollapsed ? '1' : '0'); } catch {}
  }, [qosCollapsed]);
  useEffect(() => {
    try { localStorage.setItem('cx-history-shown', showHistory ? '1' : '0'); } catch {}
  }, [showHistory]);
  useEffect(() => {
    try { localStorage.setItem('cx-history-collapsed', historyCollapsed ? '1' : '0'); } catch {}
  }, [historyCollapsed]);
  // When a call transitions back to IDLE (ended), bump the refresh key so the
  // history panel re-fetches and the just-ended call appears.
  useEffect(() => {
    if (sip.callState === CallState.IDLE) {
      setHistoryRefreshKey((k) => k + 1);
    }
  }, [sip.callState]);
  
  // Listen for settings event
  useEffect(() => {
    const handleSettings = () => setShowSettings(true);
    window.addEventListener('cx-open-settings', handleSettings);
    return () => window.removeEventListener('cx-open-settings', handleSettings);
  }, []);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const key = e.key.toUpperCase();
      
      if (key === CONFIG.shortcuts.mute && sip.callState === CallState.CONNECTED) {
        e.preventDefault();
        sip.toggleMute();
      }
      if (key === CONFIG.shortcuts.hold && sip.callState === CallState.CONNECTED) {
        e.preventDefault();
        sip.isLocalHold ? sip.unhold() : sip.hold();
      }
      if (key === CONFIG.shortcuts.hangup && sip.callState !== CallState.IDLE) {
        e.preventDefault();
        sip.hangup();
      }
      if (key === CONFIG.shortcuts.answer && sip.callState === CallState.INCOMING) {
        e.preventDefault();
        sip.answer();
      }
      if (key === CONFIG.shortcuts.transfer && sip.callState === CallState.CONNECTED) {
        e.preventDefault();
        setShowTransfer(true);
      }
      if (key === CONFIG.shortcuts.dialpad) {
        e.preventDefault();
        setShowDialpad(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sip]);
  
  // Handle login
  const handleLogin = useCallback((e) => {
    e.preventDefault();
    if (extension && password) {
      sip.connect(extension, password);
      onLogin?.(extension);
    }
  }, [extension, password, sip, onLogin]);
  
  // ---------------------------------------------------------------
  // Fetch SIP credentials + auto-register on mount.
  // ---------------------------------------------------------------
  const fetchAndConnect = useCallback(async () => {
    try {
      setCredsState('loading');
      setCredsError(null);
      const bundle = await softphoneApi.credentials();
      credsRef.current = bundle;
      setCredsState('ready');
      // Phase H.1: prefer the WSS endpoint of the currently active cluster
      // (first entry of wss_endpoints from the server). Falls back to the
      // static CONFIG.servers list in useSIP if not present (legacy).
      const activeWss = bundle.wss_endpoints?.[0]?.uri || null;
      // Hand the freshly-issued credentials to JsSIP via the existing connect()
      // hook. JsSIP / hold / transfer / SDP logic all unchanged.
      sip.connect(bundle.sip_username, bundle.sip_password, activeWss);
      onLogin?.(bundle.sip_username);
      // Schedule a silent refresh before the bundle expires so a long-lived
      // session never runs out of credentials.
      const ttl = Math.max(bundle.ttl_seconds || 900, 120);
      const fireInMs = Math.max((ttl - BUNDLE_REFRESH_MARGIN_SEC) * 1000, 60_000);
      if (credsTimerRef.current) clearTimeout(credsTimerRef.current);
      credsTimerRef.current = setTimeout(() => {
        softphoneApi.credentials()
          .then((b) => { credsRef.current = b; })
          .catch((err) => console.warn('SIP bundle silent refresh failed', err));
      }, fireInMs);
    } catch (err) {
      console.error('SIP credentials fetch failed', err);
      setCredsState('error');
      setCredsError(
        err?.status === 409 ? 'no_softphone_extension'
        : err?.status === 401 ? 'session_expired'
        : (err?.detail || err?.message || 'fetch_failed')
      );
    }
  }, [sip, onLogin]);

  useEffect(() => {
    fetchAndConnect();
    return () => {
      if (credsTimerRef.current) {
        clearTimeout(credsTimerRef.current);
        credsTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase H.1: react to cluster failover events. The SSE channel publishes
  // `cluster.changed` when an admin promotes a different DC; we re-fetch
  // credentials (new wss_endpoints ordering) and switch the JsSIP session
  // to the new active Kamailio. Agent sees ~2-3s "Reconnecting" status.
  useEffect(() => {
    const onClusterChanged = async (e) => {
      try {
        console.info('[CX-Softphone] cluster.changed received', e.detail);
        const bundle = await softphoneApi.credentials();
        credsRef.current = bundle;
        const newWss = bundle.wss_endpoints?.[0]?.uri;
        if (!newWss) {
          console.warn('[CX-Softphone] cluster.changed: no wss_endpoint in new bundle');
          return;
        }
        console.info('[CX-Softphone] switching to', newWss, 'active cluster:', bundle.active_cluster);
        sip.switchCluster?.(newWss, bundle.sip_username, bundle.sip_password);
      } catch (err) {
        console.warn('[CX-Softphone] cluster.changed re-fetch failed', err);
      }
    };
    window.addEventListener('cx-cluster-changed', onClusterChanged);
    return () => window.removeEventListener('cx-cluster-changed', onClusterChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sip]);

  // Reset multi-line state when the agent's call fully ends (back to IDLE
  // with no active session), so a stale line-bar doesn't linger.
  //
  // 2026-08-10 RACE FIX: during a merge->conference re-join, callState
  // TRANSIENTLY passes through IDLE (old queue leg tears down) before the
  // agent's auto-answered conf-join leg connects and flips it back to
  // CONFERENCING. A naive "clear on IDLE" wiped mlState.conf on that blip —
  // killing the ConferenceBar + the +Call button for the rest of the call,
  // intermittently (depended on SIP-event interleave). Fix: DEBOUNCE the
  // reset — only clear multi-line state if callState is STILL IDLE after a
  // short settle (the conf re-join lands well within this window). A genuine
  // call-end stays IDLE and clears normally; a merge blip does not.
  // Track the latest callState so the debounced reset can re-check it at
  // fire time (closures capture a stale value otherwise).
  const callStateRef = useRef(sip.callState);
  useEffect(() => { callStateRef.current = sip.callState; }, [sip.callState]);
  useEffect(() => {
    if (sip.callState !== CallState.IDLE) return;
    // Only reset multi-line state if callState is STILL IDLE after a settle.
    // A merge->conference re-join flips callState away from IDLE within ~1-2s,
    // so a transient IDLE blip never wipes mlState.conf (the +Call/ConferenceBar
    // bug). A genuine call-end stays IDLE and clears normally.
    const t = setTimeout(() => {
      if (callStateRef.current !== CallState.IDLE) return;  // re-joined -> keep state
      setMlState((s) => (s.hasThird || s.active !== 'caller' || s.conf)
        ? { active: 'caller', hasThird: false, thirdNumber: '', conf: null } : s);
    }, 2500);
    return () => clearTimeout(t);
  }, [sip.callState]);

  // 2026-08-10 N-party conference: backend publishes a `conference-participants`
  // SSE (via useSIP -> cx-conference-participants window event) on every
  // ConfbridgeJoin/Leave. Keep mlState.conf in sync so the ConferenceBar can
  // relabel Conference<->survivor-number + reflect party count. Only react to
  // events for the room we're currently in.
  useEffect(() => {
    const onParticipants = (e) => {
      const p = e.detail || {};
      setMlState((s) => {
        if (!s.conf) return s;                         // not in a conference
        if (p.room && s.conf.room && p.room !== s.conf.room) return s;
        return {
          ...s,
          conf: {
            ...s.conf,
            partyCount: typeof p.party_count === 'number' ? p.party_count : s.conf.partyCount,
            survivorNumber: p.survivor_number || null,
          },
        };
      });
    };
    window.addEventListener('cx-conference-participants', onParticipants);
    return () => window.removeEventListener('cx-conference-participants', onParticipants);
  }, []);

  // Handle dial
  const handleDial = useCallback(() => {
    if (dialNumber) {
      if (confAddMode) {
        // 2026-08-10: Add Call FROM INSIDE a conference. The SERVER steps the
        // agent out to a private consult line; the conferenced parties keep
        // talking (no MOH while >=2 remain). The browser places NO 2nd call.
        console.log('[CX] Conf Add Call: server-side add-party', dialNumber);
        const num = dialNumber;
        softphoneApi.conferenceAddParty(num)
          .then((r) => {
            console.log('[CX-ML] conferenceAddParty result', r);
            try { window.dispatchEvent(new CustomEvent('cx-cancel-wrap')); } catch (_) {}
            if (r && r.ok) {
              setMlState((s) => s.conf ? {
                ...s,
                conf: { ...s.conf, consultActive: true, consultNumber: num,
                        activeLine: 'consult' },
              } : s);
            } else if (r && r.reason === 'appearance_cap_reached') {
              sip.setError?.('Maximum of 4 lines reached.');
            } else {
              sip.setError?.(`Add Call failed: ${r?.reason || 'unknown'}`);
            }
          })
          .catch((e) => console.warn('[CX-ML] conferenceAddParty failed', e));
        setConfAddMode(false);
        setAddCallMode(false);
        setLastDialed(num);
        try { localStorage.setItem('cx-last-dialed', num); } catch {}
        setDialNumber('');
        setShowDialpad(false);
        return;
      }
      if (addCallMode) {
        // 2026-07-27 SERVER-SIDE multi-line: the agent's ONE browser leg stays
        // put. Ask the SERVER to park the caller on MOH and originate+bridge
        // the 3rd party to the agent. The browser places NO 2nd call.
        console.log('[CX] Add Call: server-side lineAdd', dialNumber);
        const num = dialNumber;
        softphoneApi.lineAdd(num)
          .then((r) => {
            console.log('[CX-ML] lineAdd result', r);
            // Cancel any wrap that may have started during the dial.
            try { window.dispatchEvent(new CustomEvent('cx-cancel-wrap')); } catch (_) {}
            if (r && r.ok) {
              setMlState((s) => ({ ...s, active: 'third', hasThird: true, thirdNumber: num }));
            } else {
              sip.setError?.(`Add Call failed: ${r?.reason || 'unknown'}`);
            }
          })
          .catch((e) => console.warn('[CX-ML] lineAdd failed', e));
        setAddCallMode(false);
        setLastDialed(num);
        try { localStorage.setItem('cx-last-dialed', num); } catch {}
        setDialNumber('');
        setShowDialpad(false);
        return;
      }
      sip.call(dialNumber);
      setLastDialed(dialNumber);
      try { localStorage.setItem('cx-last-dialed', dialNumber); } catch {}
      setDialNumber('');
      setShowDialpad(false);
    }
  }, [dialNumber, sip, addCallMode, confAddMode]);

  // Handle redial - dial the last-dialed number directly
  const handleRedial = useCallback(() => {
    if (lastDialed && sip.callState === CallState.IDLE) {
      sip.call(lastDialed);
      setDialNumber('');
      setShowDialpad(false);
    }
  }, [lastDialed, sip]);
  
  // Handle dialpad input
  const handleDialpadClick = (digit) => {
    // In Add Call mode we're composing the 3rd-party number, so build the
    // string instead of sending DTMF to the current caller.
    if (addCallMode) {
      setDialNumber(prev => prev + digit);
      return;
    }
    if (sip.callState === CallState.CONNECTED) {
      sip.sendDTMF(digit);
    } else {
      setDialNumber(prev => prev + digit);
    }
  };
  
  // Handle "Add Call" — mirror Transfer > Conference without any dialog.
  // Steps (per Fernando's spec 2026-07-26):
  //   1. If the caller is currently on Hold, resume them first so we start
  //      from the same CONNECTED state a manual conference starts from.
  //   2. Enter add-call mode + reveal the dialpad so the agent types the
  //      3rd-party number in the normal UI (no transfer/conference window).
  //   3. When the agent presses Call, handleDial() routes the number through
  //      sip.startConference() — identical to Transfer > Conference.
  const handleAddCall = useCallback(async () => {
    // 2026-07-27: cancel any pending wrap-up countdown — the agent is starting
    // a consult, NOT ending the call. A stale wrap timer finishing mid-consult
    // flips to Ready + mutes the JsSIP session -> one-way audio (root cause of
    // the Hold->Add Call one-way bug).
    try { window.dispatchEvent(new CustomEvent('cx-cancel-wrap')); } catch (_) {}
    // 2026-07-27: if the agent is on the (server-side) Hold, UNHOLD FIRST via
    // the browser's own unhold path (which restores clean 2-way audio via
    // _reattachRemoteAudio) and let media settle, THEN start Add Call. This
    // avoids the one-way audio from the server-side hold having manipulated
    // the agent leg's media before the consult bridges in. (Per Fernando:
    // resume, wait ~1s, then continue the Add Call sequence.)
    if (sip.isLocalHold) {
      console.log('[CX-ML] Add Call while on hold -> unhold first, then dial mode');
      try { sip.unhold(); } catch (_) {}
      await new Promise((r) => setTimeout(r, 1200));
    }
    // SERVER-SIDE multi-line: Add Call just opens the dialpad; on Call
    // (handleDial -> lineAdd) the SERVER parks the caller on MOH + originates
    // + bridges the 3rd party to the agent's ONE browser leg.
    setAddCallMode(true);
    setDialNumber('');
    setShowDialpad(true);
  }, [sip]);

  // Swap which line the agent is talking to (server-side bridge swap).
  const handleSwapLine = useCallback(async (to) => {
    try {
      const r = await softphoneApi.lineSwap(to);
      console.log('[CX-ML] swap ->', to, r);
      if (r && r.ok) setMlState((s) => ({ ...s, active: to }));
    } catch (e) { console.warn('[CX-ML] swap failed', e); }
  }, []);

  // Hang up one line ('caller'|'third'); the other becomes the active 1:1 call.
  const handleHangupLine = useCallback(async (which) => {
    try {
      const r = await softphoneApi.lineHangup(which);
      console.log('[CX-ML] hangup line', which, r);
      if (r && r.ok) {
        if (which === 'third') {
          setMlState({ active: 'caller', hasThird: false, thirdNumber: '' });
        } else {
          // caller dropped; 3rd party becomes the (only) active call
          setMlState((s) => ({ active: 'third', hasThird: false, thirdNumber: s.thirdNumber }));
        }
      }
    } catch (e) { console.warn('[CX-ML] hangup line failed', e); }
  }, []);

  // Merge the two lines into a real ConfBridge conference.
  const handleMergeLines = useCallback(async () => {
    try {
      const r = await softphoneApi.lineMerge();
      console.log('[CX-ML] merge ->', r);
      if (r && r.ok) {
        // Conference established. Replace the 2-line bar with the N-party
        // conference appearance (ConferenceBar). Mark the softphone as MERGED
        // so CallControls also shows "Release me"/"End all". The agent rejoins
        // as a fresh auto-answered conf leg. partyCount starts at 2 (caller +
        // 3rd party); the participant SSE keeps it accurate thereafter.
        setMlState({
          active: 'caller', hasThird: false, thirdNumber: '',
          conf: {
            room: r.room, partyCount: 2, survivorNumber: null,
            consultActive: false, consultNumber: '',
          },
        });
        try { sip.markMerged?.(r.room); } catch (_) {}
      }
    } catch (e) { console.warn('[CX-ML] merge failed', e); }
  }, [sip]);

  // ---- N-party conference (2026-08-10) --------------------------------

  // Add Call FROM INSIDE the conference: open the dialpad; on Call the SERVER
  // steps the agent out to a private consult line while the conferenced
  // parties keep talking. We set a flag so handleDial routes to the
  // conference add-party endpoint instead of the 2-line lineAdd.
  const handleConfAddCall = useCallback(async () => {
    try { window.dispatchEvent(new CustomEvent('cx-cancel-wrap')); } catch (_) {}
    setConfAddMode(true);
    setAddCallMode(true);
    setDialNumber('');
    setShowDialpad(true);
  }, []);

  // Merge the pending consult line into the existing conference room.
  const handleMergeParty = useCallback(async () => {
    try {
      const r = await softphoneApi.conferenceMergeParty();
      console.log('[CX-ML] merge-party ->', r);
      if (r && r.ok) {
        setMlState((s) => s.conf ? {
          ...s,
          conf: { ...s.conf, consultActive: false, consultNumber: '',
                  activeLine: 'conference',
                  partyCount: (s.conf.partyCount || 2) + 1 },
        } : s);
      }
    } catch (e) { console.warn('[CX-ML] merge-party failed', e); }
  }, []);

  // Hold the consult line (park on MOH) and return to the conference.
  const handleConsultHold = useCallback(async () => {
    try {
      const r = await softphoneApi.conferenceConsultReturn('hold');
      console.log('[CX-ML] consult hold ->', r);
      if (r && r.ok) {
        setMlState((s) => s.conf ? {
          ...s, conf: { ...s.conf, consultActive: false, activeLine: 'conference' },
        } : s);
      }
    } catch (e) { console.warn('[CX-ML] consult hold failed', e); }
  }, []);

  // Terminate the consult line and return to the conference.
  const handleConsultHangup = useCallback(async () => {
    try {
      const r = await softphoneApi.conferenceConsultReturn('hangup');
      console.log('[CX-ML] consult hangup ->', r);
      if (r && r.ok) {
        setMlState((s) => s.conf ? {
          ...s, conf: { ...s.conf, consultActive: false, consultNumber: '',
                        activeLine: 'conference' },
        } : s);
      }
    } catch (e) { console.warn('[CX-ML] consult hangup failed', e); }
  }, []);

  // Switch audio back to the conference while a consult line is on hold.
  // (Swapping to the consult line is not yet a distinct backend op; Hold
  // parks the consult so the agent is in the conference. Full back-and-forth
  // swap between the conference and a held consult is a follow-up.)
  const handleSwapToConference = useCallback(async () => {
    setMlState((s) => s.conf ? {
      ...s, conf: { ...s.conf, activeLine: 'conference' },
    } : s);
  }, []);

  // Handle transfer
  const handleTransfer = useCallback((target, type) => {
    if (type === TransferType.BLIND) {
      sip.blindTransfer(target);
    } else {
      sip.attendedTransfer(target);
    }
    setShowTransfer(false);
  }, [sip]);
  
  // ═══════════════════════════════════════════════════════════════
  // RENDER: Login Form
  // ═══════════════════════════════════════════════════════════════
  
  // Phase 1: SIP credentials are fetched from /api/softphone/credentials; we
  // no longer show a typed login form. The states below cover loading,
  // errors fetching the bundle, and JsSIP-level connection failures.
  if (credsState === 'loading') {
    return (
      <div className="cx-softphone cx-softphone--connecting">
        <div className="cx-softphone__spinner" />
        <p className="cx-softphone__connecting-text">Loading softphone credentials…</p>
      </div>
    );
  }

  if (credsState === 'error') {
    // No-extension users (viewers / supervisors-without-ext / client users) shouldn't
    // see the "Softphone unavailable" wall. If they have any other allowed module,
    // bounce them to the landing redirect which picks their best default page.
    if (credsError === 'no_softphone_extension') {
      return <NoExtensionRedirect />;
    }
    const friendly =
      credsError === 'session_expired'
        ? 'Your portal session expired. Please reload and sign in again.'
        : `Could not load softphone credentials: ${credsError}`;
    return (
      <div className="cx-softphone cx-softphone--login">
        <div style={{ padding: 24, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
          <h3 style={{ marginBottom: 12 }}>Softphone unavailable</h3>
          <p style={{ color: '#6B7280', marginBottom: 16 }}>{friendly}</p>
          <button onClick={fetchAndConnect} className="cx-softphone__footer-btn">↻ Try again</button>
        </div>
      </div>
    );
  }

  if (sip.connectionState === ConnectionState.DISCONNECTED ||
      sip.connectionState === ConnectionState.ERROR) {
    // Distinguish intentional logout from a real failure.
    if (sip.didUserLogout) {
      return (
        <div className="cx-softphone cx-softphone--login">
          <div style={{ padding: 24, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
            <h3 style={{ marginBottom: 12 }}>Signed out</h3>
            <p style={{ color: '#6B7280', marginBottom: 16 }}>
              You’ve been signed out of the softphone. Click Reconnect when you’re ready to take calls again.
            </p>
            <button
              onClick={() => (sip.reconnect ? sip.reconnect() : fetchAndConnect())}
              className="cx-softphone__footer-btn"
            >
              ↻ Reconnect
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="cx-softphone cx-softphone--login">
        <div style={{ padding: 24, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
          <h3 style={{ marginBottom: 12 }}>SIP connection error</h3>
          <p style={{ color: '#E53935', marginBottom: 16 }}>{sip.error || 'Registration failed.'}</p>
          <button onClick={fetchAndConnect} className="cx-softphone__footer-btn">↻ Reconnect</button>
        </div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════
  // RENDER: Connecting
  // ═══════════════════════════════════════════════════════════════
  
  if (sip.connectionState === ConnectionState.CONNECTING) {
    return (
      <div className="cx-softphone cx-softphone--connecting">
        <div className="cx-softphone__spinner" />
        <p className="cx-softphone__connecting-text">
          Connecting to {sip.currentServer?.name}...
        </p>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════
  // RENDER: Main Softphone
  // ═══════════════════════════════════════════════════════════════
  
  // Wrap softphone card + QoS sidecar in a horizontal flex row. The outer
  // container fills the page area but DOES NOT scroll — the softphone card
  // itself scrolls internally if its contents exceed viewport height (e.g.
  // when an incoming call adds the dialpad row). This keeps the card pinned
  // in view regardless of call state.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 12,
        padding: 12,
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
    <div
      className="cx-softphone"
      style={{
        position: 'relative',
        maxHeight: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* QoS toggle button — top-right corner of the softphone card */}
      <button
        onClick={() => {
          if (!showQos) {
            // Opening for the first time always uses expanded view
            setShowQos(true);
            setQosCollapsed(false);
          } else {
            setShowQos(false);
          }
        }}
        title={showQos ? 'Hide QoS panel' : 'Show live call QoS (RTCP stats)'}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          padding: '3px 8px',
          fontSize: 11,
          fontWeight: 600,
          borderRadius: 4,
          border: '1px solid var(--cx-border, #e4e7eb)',
          background: showQos ? '#5AB6FF' : 'var(--cx-panel, #fff)',
          color: showQos ? '#fff' : 'var(--cx-text, #1a202c)',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
        }}
      >
        📶 QoS
      </button>

      {/* Call History toggle button — top-right, left of the QoS button */}
      <button
        onClick={() => {
          if (!showHistory) {
            setShowHistory(true);
            setHistoryCollapsed(false);
            setHistoryRefreshKey((k) => k + 1);
          } else {
            setShowHistory(false);
          }
        }}
        title={showHistory ? 'Hide call history' : 'Show your call history (this shift) — caller IDs of past calls'}
        style={{
          position: 'absolute',
          top: 8,
          right: 74,
          zIndex: 10,
          padding: '3px 8px',
          fontSize: 11,
          fontWeight: 600,
          borderRadius: 4,
          border: '1px solid var(--cx-border, #e4e7eb)',
          background: showHistory ? '#5AB6FF' : 'var(--cx-panel, #fff)',
          color: showHistory ? '#fff' : 'var(--cx-text, #1a202c)',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
        }}
      >
        📞 History
      </button>

      {/* ACD State Bar (Phase 2): Ready / AUX / Wrap-up / Logout — PINNED TOP.
          Phase H.1 (2026-06-19): AgentStatusBar must stay mounted across
          cluster failovers. Previously it was conditional on
          `sip.connectionState === REGISTERED` which caused it to unmount
          while the SIP UA was being swapped during switchCluster(). The
          remount fired the mount effect's `softphoneApi.setState('logout')`
          call and flipped the agent to Not Ready. Now we render it always
          but hide it visually when the SIP session is NOT registered, so
          its lifecycle state survives the UA swap. */}
      <div style={{ flex: '0 0 auto', display: sip.connectionState === ConnectionState.REGISTERED ? 'block' : 'none' }}>
        <AgentStatusBar
          extension={credsRef.current?.sip_username || ''}
          callActive={!!sip.callState && sip.callState !== 'idle' && sip.callState !== 'ended'}
          callState={sip.callState}
          callDirection={sip.callInfo?.direction || null}
          /* 2026-08-20: tell AgentStatusBar when a multi-line engagement is in
             progress (held caller / active consult / 3rd party / conference)
             so it HARD-SUPPRESSES the wrap-up trigger. An Add-Call transiently
             dips SIP callState to idle, which previously started wrap MID-
             CONSULT and tore down the held caller. */
          multiLineActive={!!(mlState.hasThird || mlState.conf
            || (mlState.active && mlState.active !== 'caller'))}
          onLoggedOut={() => {
            // After logout, disconnect the SIP session too so we look
            // "signed out" both at AMI and at the registrar.
            sip.disconnect?.();
          }}
        />
      </div>

      {/* 2026-07-15: mandatory screen-recording gate. Renders a modal overlay
          for agents whose `screen_recording_enabled=true` in the DB and who
          haven't yet granted getDisplayMedia permission. AgentStatusBar has
          already published `window.__cxScreenRec`, so the gate consumes
          that handle and calls acquireStream(). Nothing renders when the
          agent's toggle is off OR they've already granted the stream. */}
      {sip.connectionState === ConnectionState.REGISTERED && (
        <ScreenShareGate
          extension={credsRef.current?.sip_username || ''}
          onLogout={() => {
            // Fire the same logout path used by the logout button.
            softphoneApi.setState('logout', null).catch(() => {});
            sip.disconnect?.();
          }}
          onBlock={() => {
            // COMPLIANCE: recording required but no live screen stream ->
            // force the agent OUT of Ready so no calls route to them
            // unrecorded. They must grant screen share to receive calls.
            console.warn('[CX-SR-GATE] onBlock -> forcing not-ready');
            softphoneApi.setState('logout', null).catch(() => {});
          }}
          onBypass={() => {
            // 2026-07-28: agent proceeded WITHOUT screen recording (tenant
            // allows bypass + Azure connectivity failed). Show a persistent
            // session banner so it's never invisible.
            console.warn('[CX-SR-GATE] onBypass -> working unrecorded');
            setScreenRecUnrecorded(true);
          }}
        />
      )}
      {screenRecUnrecorded && (
        <div
          style={{
            background: '#FEF3C7', color: '#7C2D12',
            border: '1px solid #F59E0B', borderRadius: 6,
            padding: '6px 12px', fontSize: 12, fontWeight: 600,
            margin: '6px 8px', textAlign: 'center',
          }}
        >
          ⚠️ Working without screen recording — storage connectivity unavailable on this network.
        </div>
      )}

      {/* SCROLLABLE MIDDLE: Call display + Dialpad.
          Action buttons (mute/hold/xfer/conf) and footer (settings/logout)
          stay pinned at the bottom so agents NEVER have to scroll to reach them. */}
      <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {/* Call Display */}
        <CallDisplay
          callState={sip.callState}
          callInfo={sip.callInfo}
          callDuration={sip.callDuration}
          callQuality={sip.callQuality}
          isMuted={sip.isMuted}
          isLocalHold={sip.isLocalHold}
          isRemoteHold={sip.isRemoteHold}
          transferState={sip.transferState}
          dialNumber={dialNumber}
          onDialNumberChange={setDialNumber}
          addCallMode={addCallMode}
          lastDialed={lastDialed}
          onRedial={handleRedial}
          onUseLastDialed={() => setDialNumber(lastDialed)}
        />

        {/* Dialpad */}
        {(showDialpad || sip.callState !== CallState.IDLE) && (
          <Dialpad
            onDigitClick={handleDialpadClick}
            disabled={sip.callState === CallState.INCOMING}
          />
        )}
      </div>

      {/* PINNED BOTTOM: Multi-line bar + Held-calls tray + Call Controls + Footer */}
      <div style={{ flex: '0 0 auto' }}>
      {mlState.hasThird && !mlState.conf && (
        <MultiLineBar
          active={mlState.active}
          callerLabel={(sip.callInfo && (sip.callInfo.remoteName || sip.callInfo.remoteNumber)) || 'Caller'}
          thirdLabel={mlState.thirdNumber || '3rd party'}
          onSwap={handleSwapLine}
          onHangup={handleHangupLine}
          onMerge={handleMergeLines}
        />
      )}
      {mlState.conf && (
        <ConferenceBar
          partyCount={mlState.conf.partyCount}
          survivorNumber={mlState.conf.survivorNumber}
          consultActive={mlState.conf.consultActive}
          consultNumber={mlState.conf.consultNumber}
          activeLine={mlState.conf.activeLine || 'conference'}
          onSwapToConference={handleSwapToConference}
          onMergeParty={handleMergeParty}
          onConsultHold={handleConsultHold}
          onConsultHangup={handleConsultHangup}
          onRelease={sip.releaseConference}
          onEndAll={sip.endAllConference}
        />
      )}
      <HeldCallsTray
        heldCalls={sip.heldCalls || []}
        parkedIncoming={sip.parkedIncoming}
        onResume={sip.resumeHeld}
        onHangupHeld={sip.hangupHeld}
        onAcceptParkedIncoming={sip.acceptParkedIncoming}
        onRejectParkedIncoming={sip.rejectParkedIncoming}
        onMergeHeld={sip.mergeHeldAndActive}
        canMergeHeld={
          sip.callState === CallState.CONNECTED
          && (sip.heldCalls?.length || 0) === 1
        }
      />
      <CallControls
        callState={sip.callState}
        isMuted={sip.isMuted}
        isLocalHold={sip.isLocalHold}
        hasConsultSession={sip.consultSession}
        transferState={sip.transferState}
        dialNumber={dialNumber}
        onDial={handleDial}
        onAnswer={sip.answer}
        onHangup={mlState.conf ? sip.endAllConference : sip.hangup}
        onToggleMute={sip.toggleMute}
        onHold={sip.hold}
        onUnhold={sip.unhold}
        onTransferClick={() => setShowTransfer(true)}
        onConferenceClick={() => setShowTransfer(true)}
        onCompleteTransfer={sip.completeAttendedTransfer}
        onCancelTransfer={sip.cancelTransfer}
        onMergeConference={sip.mergeConference}
        onReleaseConference={sip.releaseConference}
        onEndAll={sip.endAllConference}
        conferenceMerged={sip.conferenceMerged}
        onToggleDialpad={() => setShowDialpad(prev => !prev)}
        showDialpad={showDialpad}
        // 2026-08-10 Option A: the N-party conference reuses the SAME single-
        // line layout. When in a conference, Add Call routes to the
        // conference add-party flow (step out to dial) instead of the 2-line
        // lineAdd; the conference itself is shown as one line (ConferenceBar).
        nPartyConf={!!mlState.conf}
        onNewCall={mlState.conf ? handleConfAddCall : handleAddCall}
        canStartNewCall={
          mlState.conf
            // In a conference: allow Add Call unless already stepped out to a
            // consult line, or at the 4-appearance cap.
            ? (!mlState.conf.consultActive
               && (((mlState.conf.partyCount || 0)
                    + (mlState.conf.consultActive ? 1 : 0)) < 4))
            : ((sip.callState === CallState.CONNECTED
                || sip.callState === CallState.HOLDING)
               && !sip.consultSession)
        }
        addCallMode={addCallMode}
        onCancelAddCall={() => {
          // 2026-07-27 SERVER-SIDE: Add Call only opened the dialpad (no call
          // placed yet, caller still bridged to agent). Cancel just closes
          // add-call mode — nothing to unwind server-side.
          setAddCallMode(false);
          setDialNumber('');
          setShowDialpad(false);
        }}
      />

      {/* Footer: Settings + Log Out */}
      <div className="cx-softphone__footer">
        <button 
          className="cx-softphone__footer-btn"
          onClick={() => setShowSettings(true)}
          title="Audio devices, preferences"
        >
          ⚙️ Settings
        </button>
        <button 
          className="cx-softphone__footer-btn cx-softphone__footer-btn--logout"
          onClick={() => {
            if (sip.callState !== CallState.IDLE) {
              if (!window.confirm('You are on a call. Log out anyway?')) return;
            }
            sip.disconnect();
          }}
          title="Sign out of softphone"
        >
          🚪 Log Out
        </button>
      </div>
      
      {/* Error display */}
      {sip.error && (
        <div className="cx-softphone__error">
          {sip.error}
        </div>
      )}
      </div>{/* end PINNED BOTTOM */}
      
      {/* Transfer Dialog */}
      {showTransfer && (
        <TransferDialog
          onClose={() => setShowTransfer(false)}
          onTransfer={handleTransfer}
          onConference={(target) => {
            sip.startConference(target);
            setShowTransfer(false);
          }}
          transferState={sip.transferState}
        />
      )}
      
      {/* Settings Panel */}
      {showSettings && (
        <SettingsPanel
          onClose={() => setShowSettings(false)}
          audioDevices={sip.audioDevices}
          selectedInput={sip.selectedInput}
          selectedOutput={sip.selectedOutput}
          onInputChange={sip.setInputDevice}
          onOutputChange={sip.setOutputDevice}
          onRefreshDevices={sip.refreshAudioDevices}
        />
      )}
    </div>

    {/* QoS sidecar — lives next to the softphone card; remains visible during
        a call and can be collapsed to a thin strip without dropping audio. */}
    {showQos && (
      <QosPanel
        callQuality={sip.callQuality}
        callActive={sip.callState && sip.callState !== CallState.IDLE}
        collapsed={qosCollapsed}
        onToggleCollapse={() => setQosCollapsed((v) => !v)}
        onClose={() => setShowQos(false)}
      />
    )}

    {/* Call History sidecar — the agent's own past calls this shift, so a CCR
        can retrieve the caller ID of a call the customer hung up on (e.g. for
        911). Mirrors the QoS panel. */}
    {showHistory && (
      <CallHistoryPanel
        collapsed={historyCollapsed}
        onToggleCollapse={() => setHistoryCollapsed((v) => !v)}
        onClose={() => setShowHistory(false)}
        refreshKey={historyRefreshKey}
      />
    )}
    </div>
  );
}

export default Softphone;


// Bounce users without an extension away from /softphone. We send them to
// /acd/floor first if they can see it, otherwise let LandingRedirect at "/"
// pick the best default for their permissions.
//
// IMPORTANT: only redirect if the user is currently ON /softphone. The
// Softphone component is mounted globally in PortalShell (hidden via CSS
// when not on /softphone). Without this guard, ANY user without an
// extension would be force-redirected to /acd/floor on every navigation,
// because the hidden Softphone keeps re-rendering this Navigate on every
// route change. (Reported by Fernando 2026-06-19; admin without extension
// was trapped on /acd/floor and could not click any sidebar item.)
function NoExtensionRedirect() {
  const location = useLocation();
  const { data: perms, isLoading } = useQuery({
    queryKey: ['permissions', 'me'],
    queryFn: () => permissionsApi.me(),
    staleTime: 60_000,
  });
  // If we're not on the softphone route, render nothing. The component is
  // hidden by PortalShell anyway, so there is nothing to redirect away from.
  if (!location.pathname.startsWith('/softphone')) return null;
  if (isLoading) return null;
  const allowed = new Set(perms?.is_admin ? ['acd_floor'] : (perms?.modules || []));
  if (allowed.has('acd_floor')) return <Navigate to="/acd/floor" replace />;
  return <Navigate to="/" replace />;
}
